/**
 * LIG — site signals for Site Health (2026-10-23).
 *
 * Anonymous step counts so /admin/site-health can compare shoppers inside the
 * Instagram / Facebook / TikTok app browsers with shoppers in a normal browser.
 * 43% of visits come from Meta and that traffic reaches checkout far less often
 * (41% vs 63% of carts), and Shopify's own reports can't split by browser.
 *
 *   visit · cart_add · checkout_click   each counted once per browser session,
 *   with: browser (app or normal), device (phone/desktop), a coarse source
 *   category (social / search / email / ad / direct / other), and the kind of
 *   page the visit started on (home / winter / pajamas / collection / product /
 *   maker / cart / other) — so one page can be compared across ad, email and
 *   direct shoppers (Mike, 10/24: email is our own audience, ads are new people).
 *
 * Nothing else leaves the page for Site Health: no ids, no cart contents, no URLs,
 * no referrer, no campaign names (the source category is worked out here and
 * only the category is kept). Campaign attribution for orders comes from
 * Shopify's own customerJourneySummary, not from this file. (One separate
 * block below, "Maker lines in the cart", tells the maker desk which custom-maker
 * lines a cart held when it opened — same consent rule, its own endpoint.)
 *
 * The checkout steps are counted by the custom pixel
 * (middleware storefront/site-signals/checkout-pixel.js), which reads the two
 * cart attributes written here (_lig_source, _lig_page, _lig_internal) so a
 * checkout keeps its source and page category and our own tests stay out.
 *
 * Our own traffic: open any page with ?lig_internal=1 once and this browser is
 * counted separately (?lig_internal=0 undoes it).
 *
 * Consent: runs only when Shopify's customer-privacy API says analytics
 * processing is allowed (=== true), the same rule as lig-merchandising.js. It
 * waits for that API to load and starts later if the shopper allows analytics.
 */
(function () {
  'use strict';
  if (window.LIGSiteSignals) return;
  window.LIGSiteSignals = {};
  var API = 'https://lig-fulfillment-middleware.vercel.app/api/site-events';
  var KEY = 'lig-signals-v1';
  var ua = navigator.userAgent || '';
  var browser = /Instagram/i.test(ua) ? 'instagram'
    : /FBAN|FBAV|FB_IAB|FBIOS|FB4A/i.test(ua) ? 'facebook'
    : /BytedanceWebview|musical_ly|TikTok/i.test(ua) ? 'tiktok'
    : /; wv\)|Snapchat|Pinterest|Line\//i.test(ua) ? 'other-inapp' : 'standard';
  var device = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'mobile' : 'desktop';
  var _fetch = window.fetch.bind(window);

  /** Which kind of page this visit STARTED on (a category of our own pages, never
   *  a URL), so a page can be compared across ad, email and direct shoppers. */
  function pageGroup(path) {
    if (path === '/' || path === '') return 'home';
    if (/^\/collections\/winter-2026/.test(path)) return 'winter';
    if (/^\/collections\/christmas-pajamas/.test(path)) return 'pajamas';
    if (/^\/collections\//.test(path)) return 'collection';
    if (/^\/products\//.test(path)) return 'product';
    if (/^\/pages\/(custom-ornaments|your-dog-storybook|make-it-theirs|your-dog-calendar|christmas-cards|personalized-gear)/.test(path)) return 'maker';
    if (/^\/cart/.test(path)) return 'cart';
    return 'other';
  }
  var landing = '';
  try {
    landing = sessionStorage.getItem('lig-landing') || '';
    if (!landing) { landing = pageGroup(location.pathname); sessionStorage.setItem('lig-landing', landing); }
  } catch (e) { landing = pageGroup(location.pathname); }

  function allowed() {
    try { return window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true; } catch (e) { return false; }
  }
  function read() { try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return {}; } }
  function write(o) { try { localStorage.setItem(KEY, JSON.stringify(o)); } catch (e) { /* private mode */ } }

  // Our own traffic flag is a functional switch, kept whatever the consent.
  var q = new URLSearchParams(location.search);
  var s = read();
  if (q.get('lig_internal') === '1') { s.internal = true; write(s); }
  if (q.get('lig_internal') === '0') { delete s.internal; write(s); }

  /** First-touch source CATEGORY only, kept 30 days; a tagged ad/email click
   *  replaces an untagged first touch. */
  function category() {
    var med = (q.get('utm_medium') || '').toLowerCase(), src = (q.get('utm_source') || '').toLowerCase();
    var ref = '';
    try { ref = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) { ref = ''; }
    if (/lifeisgoldenco\.com$|myshopify\.com$/.test(ref)) ref = '';
    if (/paid|cpc|ppc|^ads?$|display/.test(med) || q.get('gclid')) return 'ad';
    if (/email|klaviyo|newsletter|sms/.test(med + ' ' + src)) return 'email';
    if (/facebook|instagram|meta|^ig$|^fb$|tiktok|pinterest|social/.test(src + ' ' + med) || q.get('fbclid') || /facebook|instagram|tiktok|pinterest|^t\.co$/.test(ref)) return 'social';
    if (/google|bing|duckduckgo|yahoo|ecosia/.test(ref)) return 'search';
    if (!ref && !src) return 'direct';
    return 'other';
  }
  function source() {
    var now = Date.now(), c = category(), tagged = c === 'ad' || c === 'email' || (c === 'social' && (q.get('utm_source') || q.get('fbclid')));
    if (!s.src || now - (s.srcAt || 0) > 30 * 864e5 || (tagged && s.src !== c)) { s.src = c; s.srcAt = now; write(s); }
    return s.src;
  }

  function send(event) {
    var body = JSON.stringify({ event: event, browser: browser, source: source(), device: device, page: landing, internal: !!s.internal });
    try { if (navigator.sendBeacon && navigator.sendBeacon(API, new Blob([body], { type: 'text/plain' }))) return; } catch (e) { /* fall back */ }
    try { _fetch(API, { method: 'POST', body: body, keepalive: true, credentials: 'omit', referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'text/plain' } }).catch(function () {}); } catch (e) { /* never break the page */ }
  }
  /** Once per browser session per step, so the counts are visits, not clicks. */
  function sendOnce(event) {
    if (!allowed()) return;
    try {
      if (sessionStorage.getItem('lig-sent-' + event)) return;
      sessionStorage.setItem('lig-sent-' + event, '1');
    } catch (e) { /* no storage: count it anyway */ }
    send(event);
  }

  var extraAttrs = {}; // e.g. a test group, set by a page test through LIGSiteSignals.tag()
  /** Page tests this visit is in ({ gt: 'a' }), kept for the browser session so a cart
   *  added on a later page still counts in the group the shopper saw. */
  var tests = {};
  try { tests = JSON.parse(sessionStorage.getItem('lig-tests') || '{}') || {}; } catch (e) { tests = {}; }
  function testSteps(step) { Object.keys(tests).forEach(function (k) { sendOnce(k + '_' + step + '_' + tests[k]); }); }
  /** Two coarse attributes on the cart so the checkout pixel can split by source
   *  and leave our own tests out. Written only after someone adds to the cart. */
  function tagCart() {
    if (!allowed()) return;
    var attrs = Object.assign({ _lig_source: source(), _lig_page: landing, _lig_internal: s.internal ? '1' : '' }, extraAttrs);
    // A test's group and the device it was seen on ("a.mobile"), so orders can be split the same way as the counts.
    Object.keys(tests).forEach(function (k) { attrs['_lig_' + k] = tests[k] + '.' + device; });
    var sig = JSON.stringify(attrs);
    try { if (sessionStorage.getItem('lig-attrs') === sig) return; } catch (e) { /* ok */ }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    _fetch(root + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attrs }) })
      .then(function (r) { if (r.ok) { try { sessionStorage.setItem('lig-attrs', sig); } catch (e) { /* ok */ } } })
      .catch(function () { /* the checkout just counts as "other" */ });
  }

  // Add-to-cart from anywhere on the site (theme forms, quick shop, makers).
  // Primary: the browser's own resource-timing log, which sees every /cart/add
  // request (fetch or XHR) whatever order other scripts wrap fetch in —
  // Shopify's event listener re-wraps it after us and may hold the native one.
  // Safari doesn't report the status, so there an attempted add counts.
  function isAdd(name) { return /\/cart\/add(\.js)?(\?|$)/.test(String(name || '').replace(/^https?:\/\/[^/]+/, '')); }
  try {
    new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (e) {
        if (!isAdd(e.name) || (e.initiatorType !== 'fetch' && e.initiatorType !== 'xmlhttprequest')) return;
        if (typeof e.responseStatus === 'number' && e.responseStatus !== 0 && (e.responseStatus < 200 || e.responseStatus >= 300)) return;
        justAdded(); sendOnce('cart_add'); testSteps('cart'); tagCart();
      });
    }).observe({ type: 'resource', buffered: true });
  } catch (e) { /* old browser: the fetch hook below still counts */ }
  // Backup: watch successful fetch POSTs to /cart/add without changing them.
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = _fetch(input, init);
    if (isAdd(url)) {
      p.then(function (r) { if (r && r.ok) { justAdded(); sendOnce('cart_add'); testSteps('cart'); tagCart(); } }).catch(function () {});
    }
    return p;
  };

  // Heading to checkout (cart drawer, cart page, quick shop, Shop Pay / wallet buttons).
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"], .lsb-qs__checkout, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button');
    if (t) { sendOnce('checkout_click'); testSteps('checkout'); tagCart(); }
  }, true);

  // For page tests (assets/lig-convert.js): count a step once per session, and put a
  // coarse test-group attribute on the cart so its orders can be told apart.
  window.LIGSiteSignals = {
    sendOnce: sendOnce,
    browser: browser,
    tag: function (attrs) { Object.keys(attrs || {}).forEach(function (k) { if (/^_lig_[a-z_]{1,24}$/.test(k)) extraAttrs[k] = String(attrs[k]).slice(0, 24); }); tagCart(); },
    /** A visit seen in a page test's group (the Winter garment test: enroll('gt', 'a'|'b'),
     *  from assets/lig-shop-filter.js). Counts `<test>_seen_<group>` once per visit; the
     *  visit's later cart add and checkout click count as `<test>_cart_<group>` and
     *  `<test>_checkout_<group>`, and the cart carries `_lig_<test>`. The cart is not
     *  touched here: its attribute is written with the first add, like the others. */
    enroll: function (test, group) {
      if (!/^[a-z]{2,8}$/.test(test) || !/^[a-z]{1,8}$/.test(group)) return;
      if (tests[test] !== group) {
        tests[test] = group;
        try { sessionStorage.setItem('lig-tests', JSON.stringify(tests)); } catch (e) { /* private mode */ }
      }
      sendOnce(test + '_seen_' + group);
    },
  };

  /* ---- Maker lines in the cart (2026-10-27, for the maker desk's funnel) ------------------------
   * When the cart drawer or the cart page opens with a custom-maker line in it (an ornament, card,
   * storybook, calendar, cuff name, custom design or Make It Theirs product), tell the maker desk,
   * once per browser session per cart, and whether it opened right after an add or the shopper
   * came back to it. Only with analytics permission, like everything above, and to the maker
   * desk's own endpoint (middleware api/maker-cart.ts), not to Site Health. What leaves, per line
   * that has properties: the property NAMES, our own design ids (_design_id, _card_id,
   * _book_edition, _calendar_design_id), the cuff names and the first custom-design photo HASHED
   * (sha256, never the words or the URL), the product id and the quantity. Nothing else. */
  var MAKER_API = 'https://lig-fulfillment-middleware.vercel.app/api/maker-cart';
  var MAKER_IDS = ['_design_id', '_card_id', '_book_edition', '_calendar_design_id'];
  var lastAdd = 0;
  function justAdded() { lastAdd = Date.now(); try { sessionStorage.setItem('lig-last-add', String(lastAdd)); } catch (e) { /* ok */ } }
  function addedAt() { var t = lastAdd; try { t = Math.max(t, Number(sessionStorage.getItem('lig-last-add')) || 0); } catch (e) { /* ok */ } return t; }
  function sha32(text) {
    try {
      return crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)).then(function (b) {
        return Array.prototype.map.call(new Uint8Array(b), function (x) { return ('0' + x.toString(16)).slice(-2); }).join('').slice(0, 32);
      }).catch(function () { return ''; });
    } catch (e) { return Promise.resolve(''); }
  }
  function describeLine(item) {
    var p = (item && item.properties) || {};
    var keys = Object.keys(p).filter(function (k) { return p[k] !== null && p[k] !== ''; });
    if (!keys.length) return Promise.resolve(null);
    var ids = {};
    MAKER_IDS.forEach(function (k) { if (typeof p[k] === 'string' && /^[a-z0-9_]{6,60}$/.test(p[k])) ids[k] = p[k]; });
    var names = [p["Dog's name"], p["Second dog's name"]].filter(Boolean).map(function (n) { return String(n).trim().toUpperCase(); }).join('|');
    var photo = String(p.design_photo_urls || '').split('\n')[0].trim();
    return Promise.all([names ? sha32(names) : '', photo ? sha32(photo) : '']).then(function (h) {
      var line = { keys: keys.slice(0, 30).map(function (k) { return k.slice(0, 40); }), ids: ids, product: item.product_id, qty: item.quantity };
      if (h[0]) line.names = h[0];
      if (h[1]) line.photo = h[1];
      return line;
    });
  }
  function makerCartViewed() {
    if (!allowed()) return;
    var how = Date.now() - addedAt() < 8000 ? 'after_add' : 'opened';
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    _fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { return Promise.all(((cart && cart.items) || []).slice(0, 30).map(describeLine)); })
      .then(function (lines) {
        lines = (lines || []).filter(Boolean);
        if (!lines.length || !allowed()) return;
        var sig = how + ':' + lines.map(function (l) { return [l.product, l.qty, l.names || '', l.photo || ''].concat(Object.keys(l.ids).map(function (k) { return l.ids[k]; })).join(','); }).join('|');
        try {
          var sent = JSON.parse(sessionStorage.getItem('lig-mk-cart') || '[]');
          if (sent.indexOf(sig) >= 0) return;
          sessionStorage.setItem('lig-mk-cart', JSON.stringify(sent.concat([sig]).slice(-20)));
        } catch (e) { /* no storage: report it anyway */ }
        var body = JSON.stringify({ event: 'cart_viewed', how: how, lines: lines, test: !!s.internal });
        try { if (navigator.sendBeacon && navigator.sendBeacon(MAKER_API, new Blob([body], { type: 'text/plain' }))) return; } catch (e) { /* fall back */ }
        _fetch(MAKER_API, { method: 'POST', body: body, keepalive: true, credentials: 'omit', referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'text/plain' } }).catch(function () {});
      })
      .catch(function () { /* optional counting never breaks the cart */ });
  }
  // The cart drawer says it opened (Horizon's dialog:open, on the drawer itself; caught on the way down).
  document.addEventListener('dialog:open', function (e) {
    var t = e.target;
    if (t && t.tagName === 'CART-DRAWER-COMPONENT') makerCartViewed();
  }, true);
  function onCartPage() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return location.pathname === root + 'cart' || location.pathname === root + 'cart/' || location.pathname === '/cart';
  }

  // The visit: as soon as consent is known to allow it (and, on the cart page, the maker lines in it).
  function start() { sendOnce('visit'); if (onCartPage()) makerCartViewed(); }
  document.addEventListener('visitorConsentCollected', function () { if (allowed()) start(); });
  if (allowed()) start();
  else {
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) { if (!err && allowed()) start(); });
    } catch (e) { /* no consent API on this page: nothing is sent */ }
  }
})();
